// src/hooks/usePlan.ts
import { useEffect, useMemo, useState, useCallback } from "react";
import { getPlan, type Plan } from "../api/billing";
import { apiGet } from "../api/client";
import type { AuthMe } from "../types/auth";

// Match backend's allowed tiers for a safe fallback when /auth/me is not ready
const PRO_SET = new Set(["pro", "paid", "enterprise", "ultra"]);

export function usePlan() {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [me, setMe] = useState<AuthMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, m] = await Promise.all([
        getPlan().catch(() => null),
        apiGet<AuthMe>("/auth/me").catch(() => null),
      ]);
      if (p) setPlan(p);
      if (m) setMe(m);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, m] = await Promise.all([
          getPlan().catch(() => null),
          apiGet<AuthMe>("/auth/me").catch(() => null),
        ]);
        if (!alive) return;
        if (p) setPlan(p);
        if (m) setMe(m);
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Source of truth: /auth/me -> is_user_pro
  const isPro = useMemo(() => {
    if (me?.is_user_pro != null) return !!me.is_user_pro;
    const t = (plan?.tier || "").toLowerCase();
    return PRO_SET.has(t);
  }, [me, plan]);

  return {
    plan,        // display status (active / trialing / etc.)
    me,          // contains is_user_pro for anything else you need
    isPro,       // use this for gating
    loading,
    error,
    ready: !loading && (!!plan || !!me),
    refresh,     // refetch both /billing/plan and /auth/me
  };
}
